import AgyleDialog from '../../../../components/modal/agyleDialog';
import {useTranslation} from 'react-i18next';
import * as React from 'react';
import {useEffect, useState} from 'react';
import {Divider, Grid} from '@mui/material';
import AgyleSelectField from '../../../../components/ui/selector/agyleSelectField';
import {ECDS_VERSION} from '../../../ward/forms/formsUtil';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import {consultantDictionary, ControlConsultantComponent} from '../../../../controls/agyle/clinitian';
import {
    DischargeDestination,
    DischargeFollowUp,
    DischargeStatus,
    SafeguardingTemplate,
} from '../../../../controls/agyle/ecds';
import {DelayReason} from '../../../../controls/agyle/dictionaries';
import AgyleCheckBox from '../../../../components/ui/checkbox/agyleCheckBox';
import {isEmpty} from 'lodash';
import {useDispatch} from 'react-redux';
import {setSnackbar} from '../../../../store/reducers/globals';
import Typography from '@mui/material/Typography';

const INIT_FORM_STATE = {
    ecdsVersion: '',
    description: '',
    clinician: null,
    dischargeDestination: null,
    dischargeFollowUp: null,
    dischargeStatus: null,
    dischargeSafeguarding: [],
    diagnosisRequired: false,
    safeguardingNotRequired: false,
    deactivated: false,
    delayReason: null,
    delayReasonSecondary: null,
    delayReasonText: '',
};

const DischargeTemplateModal = ({open, onClose, defaultEcdsVer, editItem, onSave}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const [state, setState] = useState({...INIT_FORM_STATE, ecdsVersion: defaultEcdsVer});
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!open) {
            setState({...INIT_FORM_STATE, ecdsVersion: defaultEcdsVer});
            setError(false);
        }
    }, [open]);

    useEffect(() => {
        if (open && editItem) {
            setState(editItem);
        }
    }, [open, editItem]);

    const valid = () => {
        return !isEmpty(state.description) && !isEmpty(state.ecdsVersion);
    };

    const handleSave = () => {
        if (valid()) {
            onSave(state);
        } else {
            setError(true);
            dispatch(setSnackbar({type: 'warning', message: t('Please fill in all required fields')}));
        }
    };

    const handleChange = (name, value) => {
        if (name === 'ecdsVersion') {
            setState(prevState => ({
                ...INIT_FORM_STATE,
                description: prevState.description,
                ecdsVersion: value,
            }));
        } else {
            setState(prevState => ({...prevState, [name]: value}));
        }
    };

    return (
        <>
            <AgyleDialog
                open={open}
                width="sm"
                disableEscapeKeyDown
                fullWidth
                noPadding
                closeButtonOnHeader
                onSave={handleSave}
                onClose={onClose}
                title={t('Discharge Template')}
            >
                <Grid style={{padding: 10, display: 'flex', flexDirection: 'column', gap: 15}}>
                    <Grid item xs={12}>
                        <AgyleTextField
                            label={t('Template Description')}
                            onChange={(e) => handleChange('description', e.target.value)}
                            value={state.description}
                            size="small"
                            fullWidth
                            noBorder={false}
                            withNewBackground
                            error={error && isEmpty(state.description)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{m: 1}}/>
                    </Grid>

                    <Grid item xs={12}>
                        <ControlConsultantComponent
                            typeDict={'DischargeClinicians'}
                            label={'Discharge Clinician'}
                            allowedConsultants={consultantDictionary.discharge}
                            value={state.clinician}
                            onChange={val => handleChange('clinician', val)}
                            withBackground={false}
                            paperBackground
                            size="small"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <DischargeStatus
                            name="dischargeStatus"
                            label={t('Discharge Status')}
                            value={state.dischargeStatus}
                            onChange={(_e, val) => handleChange('dischargeStatus', val)}
                            size="small"
                            noBorder={false}
                            withNewBackground
                            ecdsVersionForce={state.ecdsVersion}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <DischargeDestination
                            name="dischargeDestination"
                            label={t('Discharge Destination')}
                            value={state.dischargeDestination}
                            onChange={(_e, val) => handleChange('dischargeDestination', val)}
                            size="small"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <DischargeFollowUp
                            name="dischargeFollowUp"
                            label={t('Discharge Follow Up')}
                            value={state.dischargeFollowUp}
                            onChange={(_e, val) => handleChange('dischargeFollowUp', val)}
                            size="small"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <SafeguardingTemplate
                            name="dischargeSafeguarding"
                            label={t('Discharge Safeguarding')}
                            value={state.dischargeSafeguarding ?? []}
                            onChange={(_e, val) => handleChange('dischargeSafeguarding', val)}
                            size="small"
                            noBorder={false}
                            withNewBackground
                            isMultiSelect
                        />
                    </Grid>

                    <Grid item xs={12} style={{
                        marginLeft: 5,
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 10,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                        <AgyleCheckBox
                            value={state.diagnosisRequired}
                            label={t('Diagnosis is not required')}
                            onChange={(val) => handleChange('diagnosisRequired', val.target.checked)}
                        />

                        <AgyleCheckBox
                            value={state.safeguardingNotRequired}
                            label={t('Safeguarding is not required')}
                            onChange={(val) => handleChange('safeguardingNotRequired', val.target.checked)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{m: 1}}/>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2" fontWeight={600} style={{marginLeft: 3}}>
                            {t('If is a delay in discharge, please provide the following information')}
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <DelayReason
                            label={t('Primary Delay Reason')}
                            withBackground={false}
                            paperBackground
                            value={state?.delayReason}
                            onChange={(e) => handleChange('delayReason', e)}
                            size="small"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <DelayReason
                            label={t('Secondary Delay Reason')}
                            withBackground={false}
                            paperBackground
                            value={state?.delayReasonSecondary}
                            onChange={(e) => handleChange('delayReasonSecondary', e)}
                            size="small"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleTextField
                            label={t('Delay Reason Text')}
                            onChange={(e) => handleChange('delayReasonText', e.target.value)}
                            value={state.delayReasonText}
                            multiline
                            fullWidth
                            rows={5}
                            size="small"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{m: 1}}/>
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleSelectField
                            label={t('ECDS Version')}
                            onChange={(val) => handleChange('ecdsVersion', val)}
                            options={ECDS_VERSION}
                            value={state.ecdsVersion}
                            noBorder={false}
                            withNewBackground
                            fullWidth
                            size="small"
                            clearButtonVisible={false}
                            error={error && isEmpty(state.ecdsVersion)}
                        />
                    </Grid>

                    <Grid item xs={12} style={{marginLeft: 5}}>
                        <AgyleCheckBox
                            value={state.deactivated}
                            label={t('Deactivated')}
                            onChange={(val) => handleChange('deactivated', val.target.checked)}
                        />
                    </Grid>
                </Grid>

            </AgyleDialog>
        </>
    );
};

export default DischargeTemplateModal;